/**
 * Microphone pitch detection via YIN (CMNDF + absolute threshold).
 * Chosen over plain autocorrelation because dan tranh plucks have
 * strong harmonics that make autocorrelation peak-picking flip to
 * 2×/0.5× the fundamental — the "zigzag octave" the player was
 * seeing. YIN's cumulative-mean-normalized difference function
 * specifically suppresses those sub-/super-octave aliases.
 */

interface PitchDetectorHandle {
  stop: () => void;
  get last(): { freq: number; confidence: number } | null;
}

const SAMPLE_RMS_THRESHOLD = 0.01;
// Musically reasonable range for voice + dan tranh strings.
const MIN_FREQ_HZ = 55;   // ~A1 — covers bass strings with margin
const MAX_FREQ_HZ = 2000; // well above the highest string + its 2nd harmonic slop
// YIN absolute threshold. Lower = stricter = more confident but may
// miss quieter sustains; 0.1 is the standard YIN paper value.
const YIN_THRESHOLD = 0.1;

function detectPitchYin(buf: Float32Array, sampleRate: number): {
  freq: number;
  confidence: number;
} | null {
  const size = buf.length;
  // RMS gate — too quiet to be meaningful.
  let rms = 0;
  for (let i = 0; i < size; i++) rms += buf[i]! * buf[i]!;
  rms = Math.sqrt(rms / size);
  if (rms < SAMPLE_RMS_THRESHOLD) return null;

  const maxLag = Math.min(Math.floor(size / 2), Math.floor(sampleRate / MIN_FREQ_HZ));
  const minLag = Math.max(2, Math.floor(sampleRate / MAX_FREQ_HZ));
  if (maxLag <= minLag + 2) return null;

  // Step 1: difference function d(τ) = Σ (x[i] - x[i+τ])².
  const d = new Float32Array(maxLag + 1);
  for (let tau = 1; tau <= maxLag; tau++) {
    let sum = 0;
    for (let i = 0; i < maxLag; i++) {
      const diff = buf[i]! - buf[i + tau]!;
      sum += diff * diff;
    }
    d[tau] = sum;
  }

  // Step 2: cumulative mean normalized difference. CMNDF is 1 at τ=0
  // and drops toward 0 at the fundamental period — the normalization
  // is what prevents the usual "pick the lowest τ that dips" trick
  // from being fooled by a half-period sub-peak.
  const cmnd = new Float32Array(maxLag + 1);
  cmnd[0] = 1;
  let runningSum = 0;
  for (let tau = 1; tau <= maxLag; tau++) {
    runningSum += d[tau]!;
    cmnd[tau] = runningSum > 0 ? (d[tau]! * tau) / runningSum : 1;
  }

  // Step 3: absolute-threshold search. First τ where CMNDF dips below
  // threshold; then descend to the local minimum of that dip. This is
  // what avoids octave errors — picking the FIRST dip below threshold
  // means we always land on the fundamental period, never on 2× (one
  // octave down) or ½× (one octave up).
  let tauEstimate = -1;
  for (let tau = minLag; tau <= maxLag; tau++) {
    if (cmnd[tau]! < YIN_THRESHOLD) {
      while (tau + 1 <= maxLag && cmnd[tau + 1]! < cmnd[tau]!) tau++;
      tauEstimate = tau;
      break;
    }
  }
  if (tauEstimate < 0) return null;

  // Step 4: parabolic interpolation around the chosen minimum for
  // sub-sample pitch precision (matters for cents accuracy).
  const x0 = cmnd[tauEstimate - 1] ?? cmnd[tauEstimate]!;
  const x1 = cmnd[tauEstimate]!;
  const x2 = cmnd[tauEstimate + 1] ?? cmnd[tauEstimate]!;
  const a = (x0 + x2 - 2 * x1) / 2;
  const b = (x2 - x0) / 2;
  const shift = a !== 0 ? -b / (2 * a) : 0;
  const trueTau = tauEstimate + shift;
  if (trueTau < 2) return null;

  const freq = sampleRate / trueTau;
  if (freq < MIN_FREQ_HZ || freq > MAX_FREQ_HZ) return null;
  const confidence = Math.max(0, Math.min(1, 1 - cmnd[tauEstimate]!));
  return { freq, confidence };
}

/**
 * Open the microphone (one-shot prompt) and start a detection loop.
 * Calls `onPitch` about 30 times a second with the latest measurement
 * (or null when signal is too quiet / unreliable).
 *
 * `onInterrupted` fires when iOS Core Audio tears the mic down on its
 * own (screen lock / app backgrounded) — see the statechange + track
 * `mute` hooks below. The caller uses it to drop its stale handle so a
 * later return re-acquires the mic cleanly.
 */
export async function startPitchDetector(
  onPitch: (result: { freq: number; confidence: number } | null) => void,
  onInterrupted?: () => void,
): Promise<PitchDetectorHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  });
  const ctx = new AudioContext();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const buf = new Float32Array(analyser.fftSize);
  let last: { freq: number; confidence: number } | null = null;
  let stopped = false;

  // Median-of-5 smoother over the last few raw estimates. A single YIN
  // misfire (spurious octave double or glitchy attack frame) is
  // outvoted by its neighbours, so the displayed note stops "lồi lên
  // cao rồi hạ xuống" — no more zigzag. We keep cents precision by
  // smoothing in log-frequency space via picking the median sample.
  const WINDOW = 5;
  const history: ({ freq: number; confidence: number } | null)[] = [];
  const medianSmooth = (
    r: { freq: number; confidence: number } | null,
  ): { freq: number; confidence: number } | null => {
    history.push(r);
    if (history.length > WINDOW) history.shift();
    const vals = history.filter((x): x is NonNullable<typeof x> => x !== null);
    // Need a quorum of real readings — otherwise the attack/decay
    // fringe would produce a phantom "held" pitch from 1 stale sample.
    if (vals.length < Math.ceil(WINDOW / 2)) return null;
    const sorted = [...vals].sort((a, b) => a.freq - b.freq);
    return sorted[Math.floor(sorted.length / 2)]!;
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const tick = () => {
    if (stopped) return;
    analyser.getFloatTimeDomainData(buf);
    const raw = detectPitchYin(buf, ctx.sampleRate);
    const result = medianSmooth(raw);
    last = result;
    onPitch(result);
    timer = setTimeout(tick, 33); // ~30 Hz
  };
  tick();

  const doStop = () => {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    try {
      source.disconnect();
      analyser.disconnect();
    } catch {
      // ignore: already disconnected on re-entry
    }
    stream.getTracks().forEach((t) => t.stop());
    void ctx.close().catch(() => {});
  };

  // iOS-reliable stop triggers. Page-visibility events are unreliable
  // on iPad screen-lock, but iOS Core Audio DOES drive the
  // AudioContext `statechange` (→ suspended) and the mic track `mute`
  // event — so wire those as additional stop triggers to guarantee the
  // mic is released and stops draining battery. onInterrupted lets the
  // caller clear its handle so a later return re-acquires cleanly.
  ctx.addEventListener('statechange', () => {
    if (ctx.state === 'suspended') {
      doStop();
      onInterrupted?.();
    }
  });
  for (const t of stream.getTracks()) {
    t.addEventListener('mute', () => {
      doStop();
      onInterrupted?.();
    });
  }

  return {
    stop: doStop,
    get last() {
      return last;
    },
  };
}

/** Convert Hz → { pitchClass, octave, cents }. Cents are relative to the
 *  nearest equal-tempered semitone. */
export function freqToNote(freq: number): {
  pitchClass: string;
  octave: number;
  cents: number;
} {
  const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const midiFloat = 69 + 12 * Math.log2(freq / 440);
  const midi = Math.round(midiFloat);
  const cents = Math.round((midiFloat - midi) * 100);
  const pitchClass = NOTE_NAMES[((midi % 12) + 12) % 12]!;
  const octave = Math.floor(midi / 12) - 1;
  return { pitchClass, octave, cents };
}
